// A part's look, decided once for every view: what a placed part (a
// ProductInstance that is not a room) is drawn as — its BODY (the solid the 3D
// view builds, a section cuts and partModel.ts projects), its ATTACHMENTS (what
// stands round the body whatever it is: a heater's guard cover and clearance
// gap) and its SYMBOL on the plan. The plan (render.ts), the 3D view
// (three/scene3d.ts), the sections (section.ts), the plan's DXF (dxfExport.ts)
// and builtModels.ts (a projected body's model) switch over these unions, each
// case its own, so the
// precedence and the fit checks live here only (docs/claude/products.md, *A
// part's look*). Pure and DOM-free (test/partLook.test.mjs).

import type {
  PartModel3d,
  ProductInstance,
} from '../shared/types';
import {
  type BuiltBody,
  type CategorySymbol,
  traitsOf,
} from '../shared/categories';
import {
  fitsFootprint,
  fittedModel,
} from '../shared/partSolids';
import {
  type DuctFanShape,
  ductFanShapeOf,
} from '../shared/ductFans';
import { conveyorFrame } from '../shared/steamConveyors';
import {
  type DoserFigure,
  doserFigureOf,
} from '../shared/doserSolids';
import { unreachable } from '../shared/exhaustive';
import {
  HEATER_COVER_COLOR,
  heaterCoverBands,
  heaterGapBox,
  heaterGuard,
  type HeaterSide,
  isHeaterBlock,
} from './geometry';
import { isTilted } from './tilt';

/** The solid a part is, in the order the first that applies wins:
 *  - `run`: a node run (salt wall, heated or sauna bench), its pieces along its nodes;
 *  - `stored`: its stored 3D model (`model3d`), on the footprint it was made for;
 *  - `heaterBlock`: a heater's or a shower's body box, floating by its Height
 *    from ground — unless it carries a drawing of its own (`drawsHeaterBlock`);
 *  - its category's built body (`traitsOf(c).body`), on figures that fit its
 *    footprint: a steam cube, a hot tub, a duct fan, a steam conveyor, an
 *    essence doser (its figures' solids alone: shared/doserSolids.ts);
 *  - `box`: its footprint, from its Height from ground up its height. */
export type PartBody =
  | { kind: 'box' }
  | { kind: 'conveyor'; throughWall: number }
  | { kind: 'doser'; figure: DoserFigure }
  | { kind: 'fan'; shape: DuctFanShape }
  | { kind: 'heaterBlock' }
  | { kind: 'hotTub' }
  | { kind: 'run' }
  | { kind: 'steamCube' }
  | { kind: 'stored'; model: PartModel3d };

export type BodyKind = PartBody['kind'];

/** A box in a part's own frame (mm from its middle). */
export interface LocalBox { x0: number; y0: number; x1: number; y1: number }

/** What stands round a part's body, whatever the body: drawn behind it, in the
 *  order listed, the same way by every view.
 *  - `cover`: the wooden guard walls outside its clearance gap, one band a
 *    covered side, from the floor to its body's top;
 *  - `gap`: the clearance kept round its body — a line on the plan, air in 3D
 *    and in a section. */
export type PartAttachment =
  | { kind: 'cover'; bands: Array<LocalBox & { side: HeaterSide }>; color: string }
  | { kind: 'gap'; box: LocalBox };

/** What the plan draws for an upright part (a tilted one is its body seen from
 *  above: render.ts `drawTiltedPart`), the first that applies winning: its
 *  category's own symbol (unless it gives way: `GIVES_WAY`), a heater block, the
 *  Flex4's panel, its body's model from above (`model`: a part carrying a stored
 *  model and no drawing of its own), its own drawing alone (`drawing`), its box. */
export type PlanSymbol =
  | 'box'
  | 'drawing'
  | 'flex4'
  | 'heaterBlock'
  | 'model'
  | CategorySymbol;

export interface PlanLook {
  /** Its raster image, drawn in its footprint in place of all else once loaded;
   *  till then (and in the DXF) it is its symbol. Never a run's. */
  image: string | null;
  symbol: PlanSymbol;
  /** It carries a DXF drawing of its own (with entities): the plan lays it over
   *  a symbol that takes one (`TAKES_DRAWING`); the DXF writes it instead. */
  drawing: boolean;
}

export interface PartLook {
  body: PartBody;
  attachments: PartAttachment[];
  plan: PlanLook;
}

/** The bodies a view can project from their solids (partModel.ts: a tilted
 *  plan, every section) — the others are their box there. */
const PROJECTED = {
  box: false,
  conveyor: true,
  doser: true,
  fan: true,
  heaterBlock: false,
  hotTub: true,
  run: false,
  steamCube: true,
  stored: true,
} as const satisfies Record<BodyKind, boolean>;

type ProjectedKind = { [K in BodyKind]: (typeof PROJECTED)[K] extends true ? K : never }[BodyKind];
export type ProjectedBody = Extract<PartBody, { kind: ProjectedKind }>;

export function isProjected(b: PartBody): b is ProjectedBody {
  return PROJECTED[b.kind];
}

/** Whether the plan lays a part's own drawing over its symbol: a symbol drawn
 *  whole (a run's, a drain's, an infrared panel's) leaves it out. */
export const TAKES_DRAWING: Record<PlanSymbol, boolean> = {
  box: true,
  canalina: false,
  drain: false,
  drawing: true,
  flex4: true,
  heatedBench: false,
  heaterBlock: true,
  hotTub: true,
  infrared: false,
  lighting: true,
  model: true,
  saltWall: false,
  saunaBench: false,
  steamCube: true,
};

/** What a part carries of its own drawing, as a view reads it. */
interface OwnDrawing {
  /** A DXF drawing or a raster image of its own. */
  drawn: boolean;
  /** Its DXF drawing has entities. */
  lines: boolean;
  picture: string | null;
}

/** A category's symbol gives way to what the part itself carries: an infrared
 *  emitter's to its stored model, a light's to a drawing of its own. */
const GIVES_WAY: Record<CategorySymbol, (inst: ProductInstance, own: OwnDrawing) => boolean> = {
  canalina: () => false,
  drain: () => false,
  heatedBench: () => false,
  hotTub: () => false,
  infrared: (inst) => !!inst.model3d,
  lighting: (_inst, own) => own.drawn,
  saltWall: () => false,
  saunaBench: () => false,
  steamCube: () => false,
};

/** A part's look: its body, its attachments and its plan symbol. */
export function partLook(inst: ProductInstance): PartLook {
  const own: OwnDrawing = { drawn: !!inst.geometry || !!inst.imageDataUrl, lines: !!inst.geometry?.entities.length, picture: inst.imageDataUrl || null };
  const body = partBody(inst, own);
  return { body, attachments: attachmentsOf(inst, own), plan: planLook(inst, own, body) };
}

/** A part's body and attachments as a view that reads no drawing sees them (a
 *  section: no section reads `geometry` or `imageDataUrl`, section.test.mjs) —
 *  as if it carried none: a heater with a drawing of its own is a heater block,
 *  its cover and all. */
export function solidLook(inst: ProductInstance): Omit<PartLook, 'plan'> {
  return { body: partBody(inst, UNDRAWN), attachments: attachmentsOf(inst, UNDRAWN) };
}

const UNDRAWN: OwnDrawing = { drawn: false, lines: false, picture: null };

function partBody(inst: ProductInstance, own: OwnDrawing): PartBody {
  if (inst.nodes && inst.nodes.length >= 2) return { kind: 'run' };
  const model = fittedModel(inst.model3d, inst.width, inst.depth);
  if (model) return { kind: 'stored', model };
  if (isHeaterBlock(inst.category, own.drawn)) return { kind: 'heaterBlock' };
  const built = traitsOf(inst.category).body;
  return (built && builtBody(built, inst)) || { kind: 'box' };
}

/** A category's built body, on the part's own figures — null (its box) when it
 *  has none, or they are not its footprint any more. */
function builtBody(kind: BuiltBody, inst: ProductInstance): PartBody | null {
  switch (kind) {
    case 'steamCube': return { kind };
    case 'hotTub': return { kind };
    case 'fan': {
      const shape = ductFanShapeOf(inst.fanModel);
      return shape && fitsFootprint(inst.width, inst.depth, shape.length, shape.width) ? { kind, shape } : null;
    }
    case 'conveyor': {
      const room = inst.throughWall, f = room != null ? conveyorFrame(room) : null;
      return room != null && f && fitsFootprint(inst.width, inst.depth, f.width, f.depth) ? { kind, throughWall: room } : null;
    }
    case 'doser': {
      const figure = doserFigureOf(inst.doserModel);
      return figure && fitsFootprint(inst.width, inst.depth, figure.width, figure.depth) ? { kind, figure } : null;
    }
    default: return unreachable(kind, 'built body');
  }
}

/** A guarded heater block's cover and gap (`heaterGuard`); a tilted part has
 *  none (no guarded category tilts, and a cover stands on the floor). */
function attachmentsOf(inst: ProductInstance, own: OwnDrawing): PartAttachment[] {
  if (!traitsOf(inst.category).guarded || !isHeaterBlock(inst.category, own.drawn) || isTilted(inst)) return [];
  const { gap, cover } = heaterGuard(inst);
  const bands = heaterCoverBands(cover, inst.width, inst.depth, gap);
  const out: PartAttachment[] = [];
  if (bands.length) out.push({ kind: 'cover', bands, color: inst.heaterCoverColor || HEATER_COVER_COLOR });
  out.push({ kind: 'gap', box: heaterGapBox(gap, inst.width, inst.depth) });
  return out;
}

function planLook(inst: ProductInstance, own: OwnDrawing, body: PartBody): PlanLook {
  const run = !!inst.nodes && inst.nodes.length >= 2;
  return { image: run ? null : own.picture, symbol: planSymbol(inst, own, body), drawing: own.lines };
}

function planSymbol(inst: ProductInstance, own: OwnDrawing, body: PartBody): PlanSymbol {
  const mine = traitsOf(inst.category).symbol;
  if (mine && !GIVES_WAY[mine](inst, own)) return mine;
  if (isHeaterBlock(inst.category, own.drawn)) return 'heaterBlock';
  if (inst.templateId === 'builtin-flex4') return 'flex4';
  if (inst.model3d && !own.lines && !own.picture && isProjected(body)) return 'model';
  return own.lines ? 'drawing' : 'box';
}
